import React, { FC } from 'react';

import {
	BUTTON_TYPES,
	EvButton,
	EvChip,
	EvFlexRow,
	EvIcon,
	EvSwitch,
	EvTable,
	EvTableColumn,
	EvTag,
	EvTooltip,
	InfoIcon,
	TAG_STATUS,
	TrashIcon
} from '@evinced-private/ui-common';

import {
	LABEL_MANAGEMENT_PANEL_REMOVE_VALUE_DAID,
	LABEL_MANAGEMENT_PANEL_VALUES_TABLE_DAID
} from 'data-automation-ids/LabelManagementAutomationIds';
import LabelValueHideIcon from 'src/resources/icons/LabelValueHide.svg?raw';

import { LabelValue } from '../../../../../types';

import {
	DASHBOARD_COLUMN_TOOLTIP_ID,
	VALUES_TABLE_COLUMN_WIDTHS,
	VALUES_TABLE_ID,
	VALUES_TABLE_PAGE_SIZE
} from './ValuesTable.constants';
import { VALUES_TABLE_LABELS } from './ValuesTable.labels';
import { ValuesTableProps } from './ValuesTable.types';

import './ValuesTable.scss';

const renderDashboardColumnHeader = (): React.ReactNode => (
	<EvFlexRow gap="SMALL" align="CENTER">
		{VALUES_TABLE_LABELS.COLUMN_DASHBOARD_VISIBILITY}
		<EvFlexRow
			as="span"
			gap="NONE"
			align="CENTER"
			data-tooltip-id={DASHBOARD_COLUMN_TOOLTIP_ID}
			data-tooltip-content={VALUES_TABLE_LABELS.COLUMN_DASHBOARD_VISIBILITY_TOOLTIP}
		>
			<EvIcon icon={InfoIcon} small />
		</EvFlexRow>
		<EvTooltip id={DASHBOARD_COLUMN_TOOLTIP_ID} place="top" type="info" />
	</EvFlexRow>
);

const getValuesTableColumns = (
	showDashboardVisibility: boolean,
	onRemoveValue: (value: string) => void,
	onToggleDashboardVisibility: (value: string) => void
): EvTableColumn[] => {
	const columns: EvTableColumn[] = [
		{
			dataField: 'value',
			text: VALUES_TABLE_LABELS.COLUMN_VALUE,
			style: { width: VALUES_TABLE_COLUMN_WIDTHS.VALUE },
			headerStyle: { width: VALUES_TABLE_COLUMN_WIDTHS.VALUE },
			formatter: (_cell: unknown, row: LabelValue) => {
				const { value, dashboardVisible, isSessionNew } = row;
				return (
					<EvFlexRow gap="SMALL" align="CENTER" wrap="WRAP">
						<EvChip
							id={value}
							label={value}
							icons={dashboardVisible === false ? { prefix: LabelValueHideIcon } : undefined}
						/>
						{isSessionNew ? (
							<EvTag status={TAG_STATUS.INFO} text={VALUES_TABLE_LABELS.NEW_VALUE_BADGE} />
						) : null}
					</EvFlexRow>
				);
			}
		}
	];

	if (showDashboardVisibility) {
		columns.push({
			dataField: 'dashboardVisible',
			text: VALUES_TABLE_LABELS.COLUMN_DASHBOARD_VISIBILITY,
			style: { width: VALUES_TABLE_COLUMN_WIDTHS.DASHBOARD_VISIBILITY },
			headerStyle: { width: VALUES_TABLE_COLUMN_WIDTHS.DASHBOARD_VISIBILITY },
			headerFormatter: renderDashboardColumnHeader,
			formatter: (_cell: unknown, row: LabelValue) => (
				<EvSwitch
					checked={row.dashboardVisible !== false}
					onChange={() => onToggleDashboardVisibility(row.value)}
					ariaLabel={`${VALUES_TABLE_LABELS.TOGGLE_VISIBLE_ARIA} for ${row.value}`}
				/>
			)
		});
	}

	columns.push({
		dataField: 'actions',
		text: VALUES_TABLE_LABELS.COLUMN_ACTIONS,
		style: { width: VALUES_TABLE_COLUMN_WIDTHS.ACTIONS },
		headerStyle: { width: VALUES_TABLE_COLUMN_WIDTHS.ACTIONS },
		formatter: (_cell: unknown, row: LabelValue) => (
			<EvFlexRow justify="CENTER" align="CENTER">
				<EvButton
					type={BUTTON_TYPES.ICON}
					title={`${VALUES_TABLE_LABELS.REMOVE_BUTTON_ARIA} ${row.value}`}
					ariaLabel={`${VALUES_TABLE_LABELS.REMOVE_BUTTON_ARIA} ${row.value}`}
					onClick={() => onRemoveValue(row.value)}
					dataAutomationId={LABEL_MANAGEMENT_PANEL_REMOVE_VALUE_DAID}
				>
					<EvIcon icon={TrashIcon} small />
				</EvButton>
			</EvFlexRow>
		)
	});

	return columns;
};

export const ValuesTable: FC<ValuesTableProps> = ({
	values,
	showDashboardVisibility = false,
	onRemoveValue,
	onToggleDashboardVisibility
}) => {
	const columns = getValuesTableColumns(
		showDashboardVisibility,
		onRemoveValue,
		onToggleDashboardVisibility
	);

	return (
		<EvTable
			dataAutomationId={LABEL_MANAGEMENT_PANEL_VALUES_TABLE_DAID}
			tableData={values}
			totalCount={values.length}
			showInfoRow={false}
			columns={columns}
			noDataIndicationMessage={VALUES_TABLE_LABELS.EMPTY_STATE}
			options={{
				keyField: 'value',
				title: VALUES_TABLE_LABELS.TABLE_TITLE,
				caption: VALUES_TABLE_LABELS.TABLE_CAPTION,
				dataType: VALUES_TABLE_ID,
				pagination: true,
				localProps: {
					defaultPage: 1,
					defaultSizePerPage: VALUES_TABLE_PAGE_SIZE
				}
			}}
		/>
	);
};
